import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import { MagnifyingGlass } from '@phosphor-icons/react';
import debounce from 'lodash/debounce';
import Input from '../input/input';
import SearchList from './searchList';
import SearchOverlay from './searchOverlay';
import { InlineLoader } from '../loaders';
import './style';

import { searchEvents } from '../../routes/landing-page/api';

const Searchbar = ({
  label,
  top = '100px',
  type,
  placeholder,
  width,
  height,
  onSelect,
  onClickBack,
  resale,
  filter = () => true,
  universal = false, 
}) => {
  const [showOverlay, setShowOverlay] = useState(false);
  const [filteredEvents, setFilteredEvents] = useState([]);
  const [value, setValue] = useState('');
  const [containerHeight, setContainerHeight] = useState(height);
  const [noResults, setNoResults] = useState(false);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(false);
  const [error, setError] = useState(null);
  const searchRef = useRef(null);
  const inputRef = useRef(null);
  const navigate = useNavigate();

  const w = type === 'global' ? '100%' : width;

  useEffect(() => {
    function handleClickOutside(event) {
      if (window.innerWidth < 650) return;

      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowOverlay(false);
        document.body.classList.remove('no-scroll');
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    function handleResize() {
      if (window.innerWidth < 650) {
        setContainerHeight(window.innerHeight - 290);
      } else {
        setContainerHeight(height);
      }
    }

    // Initial resize calculation
    handleResize();

    // Add event listener for resize
    window.addEventListener('resize', handleResize);

    // Cleanup event listener on component unmount
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [height]);

  const handleBack = () => {
    onClickBack();
    setShowOverlay(false);
    setValue('');
    setFilteredEvents([]);
    setNoResults(false);
    setInitialLoading(false);
    setError(null);
    document.body.classList.remove('no-scroll');
  };

  const handleSelect = (_id, isResale) => {
    onSelect(_id, isResale);
    setShowOverlay(false);
    setValue('');
    setFilteredEvents([]);
    setNoResults(false);
    setLoading(false);
    setInitialLoading(false);
    setError(null);
    document.body.classList.remove('no-scroll');
  };

  const handleFocus = () => {
    setInitialLoading(true);
    searchEvents('', '', (data) => {
      setFilteredEvents((data || []).filter(filter));
      setInitialLoading(false);
    }, resale ? { resaleEvents: true, location: '' } : {}, universal);

    setShowOverlay(true);
    window.scrollTo(0, 0);
    document.body.classList.add('no-scroll');
  };

  const debouncedSearchEvents = useRef(
    debounce((searchValue) => {
      if (window.fbq && searchValue.trim().length > 0) {
        window.fbq("track", "Search", {
          search_string: searchValue,
          content_category: "event"
        });
      }
      searchEvents(searchValue, '', (data) => {
        setFilteredEvents((data || []).filter(filter));
        setLoading(false);
      }, resale ? { resaleEvents: true, location: '' } : {}, universal);
    }, 200)
  ).current;

  useEffect(() => {
    // Flush debounce on unmount to ensure last value is sent
    return () => {
      debouncedSearchEvents.flush && debouncedSearchEvents.flush();
    };
  }, []);

  const handleSearchChange = async (e) => {
    const value = e.target.value;
    setValue(value);
    setLoading(true);
    debouncedSearchEvents(value); // Always pass the latest value
    setError(null);
  };

  useEffect(() => {
    if (showOverlay && inputRef.current) {
   -inputRef.current.focus();
    }
  }, [showOverlay]);

  const renderSearchList = () => (
    
    <div
      className="w-full flex flex-col gap-2 overflow-y-scroll"
      style={{ maxHeight: containerHeight }}
    >
      {loading || initialLoading ? (
        <InlineLoader size={30} color="#FF2459" className="mx-auto" />
      ) : value !== '' && filteredEvents.length === 0 ? (
        <p className="label-16">
          Can't find your event?{' '}
          <a
            onClick={(e) => {
              e.preventDefault();
              navigate('/request');
            }}
            className="underline text-primary cursor-pointer"
          >
            Request to add the event
          </a>
        </p>
      ) : (
        filteredEvents?.map((event, index) => (
          <SearchList
            key={event._id ? event._id : event.id}
            _id={event._id ? event._id : event.id}
            date={event.start_date?.replace(' ', 'T')}
            title={event.name}
            location={event.isPrimary ? event?.event_address?.name : event.venue}
            imageUrl={event.image}
            resale={!event.isPrimary}
            type={type}
            onSelect={handleSelect}
          />
        ))
      )}
    </div>
  );

  return (
    <>
      <div className="flex flex-col gap-2"
        style={{ width: w }}>
        <Input
          width="100%"
          value={value}
          placeholder={placeholder}
          iconLeft={<MagnifyingGlass size={24} />}
          onClick={handleFocus}
          onFocus={handleFocus}
          onChange={handleSearchChange}
          autoComplete="off"
          label={label}
        />
        {type !== 'global' && (
          <div
            className={`relative w-full ${showOverlay ? 'opacity-100 visible' : 'opacity-0 invisible'}`}
            ref={searchRef}
          >
            <div className="w-full flex flex-col gap-3 py-5 items-start rounded-[12px] bd-3 z-10 absolute">
              <p className="text-grey-70 label-16 px-6 label-16">
                {value === '' && (initialLoading ? 'Loading...' : 'Trending searches 🔥')}
              </p>
              {renderSearchList()}
            </div>
          </div>
        )}
      </div>

      {(type === 'global' || type === 'mobile') && (
        <SearchOverlay
          top={top}
          width={width}
          showOverlay={showOverlay}
          handleBack={handleBack}
          searchRef={searchRef}
          inputRef={inputRef}
          value={value}
          placeholder={placeholder}
          handleSearchChange={handleSearchChange}
          renderSearchList={renderSearchList}
        />
      )}
    </>
  );
};

Searchbar.propTypes = {
  events: PropTypes.arrayOf(
    PropTypes.shape({
      date: PropTypes.string,
      month: PropTypes.string,
      title: PropTypes.string,
      location: PropTypes.string,
      time: PropTypes.string,
      imageUrl: PropTypes.string,
      ticketCount: PropTypes.string,
      resale: PropTypes.bool,
    }),
  ).isRequired,
};

Searchbar.defaultProps = {
  width: '800px',
  height: '316px',
  placeholder: 'Search by event',
  events: [],
};

export default Searchbar;
